import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from 'urql';
import { Product, ProductVariant } from '../types/ProductType';
import { ADD_TO_CART_MUTATION } from '../graphql/mutations/addToCartMutation';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { useAppDispatch } from '../app/hooks';
import { startLoading, stopLoading } from '../features/loading/loadingSlice';
import { useCartRefetch } from '../hooks/useCartRefetch';
import ImageWithFallback from './ImageWithFallback';

type ProductCardWithQuickAddProps = {
  product: Product;
  onAddToCart?: (success: boolean, message: string) => void;
};

export const ProductCardWithQuickAdd: React.FC<ProductCardWithQuickAddProps> = ({ 
  product, 
  onAddToCart 
}) => {
  const isAuth = useSelector((state: RootState) => state.userLogin.isAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    product.variants && product.variants.length > 0 ? product.variants[0] : null
  );
  
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const dispatch = useAppDispatch();
  const { refetchCart } = useCartRefetch();

  const [, addToCart] = useMutation(ADD_TO_CART_MUTATION);

  const getEffectiveMinOrderQty = () => {
    return product.min_order_qty || 1;
  };


  const getEffectiveDeliveryDate = () => {
    return  product.delivery_date || 7; // default 7 days
  };

  const handleAddToCart = async () => {
    if (isAdding) return;
    if (!isAuth) {
      try {
        const payload = {
          kind: 'productList',
          variables: {
            product_id: product.id,
            variant_id: selectedVariant?.id || null,
            quantity: quantity
          }
        };
        localStorage.setItem('pendingAddToCart', JSON.stringify(payload));
      } catch {}
      onAddToCart?.(false, 'Vui lòng đăng nhập để thêm vào giỏ hàng');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    const minOrderQty = getEffectiveMinOrderQty();
    const deliveryDate = getEffectiveDeliveryDate();

    if (quantity < minOrderQty) {
      onAddToCart?.(false, `Vui lòng chọn tối thiểu ${minOrderQty} sản phẩm`);
      return;
    }

    
    setIsAdding(true);
    dispatch(startLoading());
    
    try {
      const result = await addToCart({
        product_id: product.id,
        variant_id: selectedVariant?.id || null,
        quantity: quantity,
        delivery_date: deliveryDate
      });

      if (result.data?.addToCart?.success) {
        onAddToCart?.(true, result.data.addToCart.message || 'Đã thêm vào giỏ hàng');
        setQuantity(minOrderQty);
        
        // Refetch cart items để cập nhật số lượng
        await refetchCart();
      } else {
        onAddToCart?.(false, result.data?.addToCart?.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      onAddToCart?.(false, 'Có lỗi xảy ra khi thêm vào giỏ hàng');
    } finally {
      setIsAdding(false);
      dispatch(stopLoading());
    }
  };

  const getDisplayPrice = () => {
    if (selectedVariant) {
      return selectedVariant.final_price || selectedVariant.price;
    }
    return product.final_price || product.price;
  };

  const getImageUrl = () => {
    return product.image || product.main_image_url;
  };

  const formatPrice = (price: number): string => {
    if (!price) return '0';
    return Number(price).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  return (
    <div className="group relative bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 flex flex-col h-full">
      {/* Product Image */}
      <div className="relative w-full overflow-hidden bg-gray-50 aspect-[4/3] max-h-[280px]">
        <Link to={`/product/${product.slug}`} className="block h-full">
          <ImageWithFallback
            src={getImageUrl()}
            alt={product.name}
            className="h-full w-full object-contain object-center group-hover:scale-105 transition-transform duration-500 ease-out p-2"
          />
        </Link>
        {/* Hover overlay effect */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 pointer-events-none"></div>
      </div>

      {/* Product Info */}
      <div className="p-4 flex flex-col flex-grow">
        {/* Product Name and Price */}
        <div className="mb-3 flex-grow">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate" title={product.name}>
              <Link to={`/product/${product.slug}`} className="hover:text-blue-600 transition-colors duration-200" title={product.name}>
                {product.name}
              </Link>
            </h3>
            <div className="flex items-baseline gap-1.5 flex-shrink-0">
              {selectedVariant ? (
                selectedVariant.final_price && selectedVariant.final_price < selectedVariant.price ? (
                  <>
                    <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
                      {formatPrice(selectedVariant.final_price || selectedVariant.price)} đ
                    </span>
                    <span className="text-xs text-gray-500 line-through whitespace-nowrap">
                      {formatPrice(selectedVariant.price)} đ
                    </span>
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                      Giảm {((1 - selectedVariant.final_price / selectedVariant.price) * 100).toFixed(0)}%
                    </span>
                  </>
                ) : (
                  <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
                    {formatPrice(selectedVariant.final_price || selectedVariant.price)} đ
                  </span>
                )
              ) : (
                product.final_price && product.final_price < product.price ? (
                  <>
                    <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
                      {formatPrice(getDisplayPrice())} đ
                    </span>
                    <span className="text-xs text-gray-500 line-through whitespace-nowrap">
                      {formatPrice(product.price)} đ
                    </span>
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                      Giảm {((1 - product.final_price / product.price) * 100).toFixed(0)}%
                    </span>
                  </>
                ) : (
                  <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
                    {formatPrice(getDisplayPrice())} đ
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        {/* Variant Selection */}
        {product.variants && product.variants.length > 0 && (
          <div className="mb-2.5">
            <label htmlFor={`variant-${product.id}`} className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
              Biến thể
            </label>
            <select
              id={`variant-${product.id}`}
              value={selectedVariant?.id || ''}
              onChange={(e) => {
                const variant = product.variants?.find(v => v.id === e.target.value);
                setSelectedVariant(variant || null);
                setQuantity(product.min_order_qty || 1);
              }}
              className="w-full text-xs border-2 border-gray-200 rounded-lg px-2.5 py-2 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 font-medium"
            >
              {product.variants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.name || variant.sku} - {formatPrice(variant.final_price || variant.price)} đ
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Quantity Input with +/- buttons */}
        <div className="mb-3">
          <label htmlFor={`quantity-${product.id}`} className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
            Số lượng
            {product.min_order_qty && (
              <span className="normal-case not-italic font-normal text-gray-500 ml-1">
                (Tối thiểu: {product.min_order_qty})
              </span>
            )}
          </label>
          <div className="flex items-center gap-1.5 w-full">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(getEffectiveMinOrderQty(), quantity - 1))}
              disabled={quantity <= getEffectiveMinOrderQty()}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-gray-700"
              aria-label="Giảm số lượng"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <input
              id={`quantity-${product.id}`}
              type="number"
              min={getEffectiveMinOrderQty()}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(getEffectiveMinOrderQty(), parseInt(e.target.value) || getEffectiveMinOrderQty()))}
              className="flex-1 min-w-0 text-center text-sm font-semibold border-2 border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
            <button
              type="button"
              onClick={() => setQuantity(quantity + 1)}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 font-semibold text-gray-700"
              aria-label="Tăng số lượng"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={isAdding}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-bold py-2.5 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none"
        >
          {isAdding ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Đang thêm...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Thêm vào giỏ hàng
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

